import { useState, useRef } from "react";
import { Link, usePage, useForm } from "@inertiajs/react";
import Flash from "@/components/Flash";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useClickOutside } from "@/hooks/useClickOutside";
import AvatarComponent from "@/components/app/Avatar";
import GoogleSignInButton from "@/components/app/GoogleSignInButton";
import OrDivider from "@/components/app/OrDivider";

// Re-export Avatar so existing imports of `{ Avatar } from "@/layout/AppLayout"` keep working.
export { default as Avatar } from "@/components/app/Avatar";

export default function AppLayout({ children }) {
  const { routes, auth } = usePage().props;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-40">
        <div className="container h-14 flex items-center justify-between">
          <Link href={routes.home} className="font-bold text-lg tracking-tight">
            GameKnight
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            {auth.authenticated ? (
              <>
                <Link href={routes.events} className="text-muted-foreground hover:text-foreground transition-colors">
                  Events
                </Link>
                <UserMenu user={auth.user} routes={routes} />
              </>
            ) : (
              <GuestMenu routes={routes} />
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Flash />
        {children}
      </main>
    </div>
  );
}

function UserMenu({ user, routes }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Account menu"
      >
        <AvatarComponent user={user} size="sm" ring />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-border bg-card shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b border-border mb-1">
            <p className="text-sm font-medium truncate">{user.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>

          <Link
            href={user.path}
            onClick={() => setOpen(false)}
            className="block px-3 py-1.5 text-sm hover:bg-muted transition-colors"
          >
            Profile
          </Link>
          <Link
            href={routes.settings}
            onClick={() => setOpen(false)}
            className="block px-3 py-1.5 text-sm hover:bg-muted transition-colors"
          >
            Settings
          </Link>

          <div className="border-t border-border mt-1 pt-1">
            <form method="POST" action={routes.logout}>
              <input type="hidden" name="_method" value="delete" />
              <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content} />
              <button type="submit" className="w-full text-left px-3 py-1.5 text-sm text-destructive hover:bg-muted transition-colors">
                Sign out
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function GuestMenu({ routes }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("signin");
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  function handleOpen() {
    setOpen((o) => !o);
    setTab("signin");
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Sign in
        <svg className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-border bg-card shadow-lg z-50 p-4">
          {/* Tab switcher */}
          <div className="flex rounded-lg bg-muted p-0.5 mb-4">
            <button
              onClick={() => setTab("signin")}
              className={`flex-1 text-sm py-1.5 rounded-md font-medium transition-colors ${tab === "signin" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Sign in
            </button>
            <button
              onClick={() => setTab("signup")}
              className={`flex-1 text-sm py-1.5 rounded-md font-medium transition-colors ${tab === "signup" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Create account
            </button>
          </div>

          {tab === "signin" ? (
            <SignInForm routes={routes} />
          ) : (
            <SignUpForm routes={routes} />
          )}
        </div>
      )}
    </div>
  );
}

function SignInForm({ routes }) {
  const form = useForm({ email_address: "", password: "" });

  function submit(e) {
    e.preventDefault();
    form.post(routes.session_create);
  }

  return (
    <div className="space-y-3">
      <GoogleSignInButton action={routes.google_auth} />
      <OrDivider />

      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="dm-email">Email</Label>
          <Input
            id="dm-email"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            value={form.data.email_address}
            onChange={(e) => form.setData("email_address", e.target.value)}
          />
          {form.errors.email_address && <p className="text-xs text-destructive">{form.errors.email_address}</p>}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="dm-password">Password</Label>
            <Link href={routes.new_password} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Forgot?
            </Link>
          </div>
          <Input
            id="dm-password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={form.data.password}
            onChange={(e) => form.setData("password", e.target.value)}
          />
          {form.errors.password && <p className="text-xs text-destructive">{form.errors.password}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={form.processing}>
          {form.processing ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

function SignUpForm({ routes }) {
  const form = useForm({
    first_name: "",
    last_name: "",
    email_address: "",
    password: "",
    password_confirmation: "",
  });

  function submit(e) {
    e.preventDefault();
    form.post(routes.signup);
  }

  return (
    <div className="space-y-3">
      <GoogleSignInButton action={routes.google_auth} />
      <OrDivider />

      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="dm-first">First name</Label>
            <Input
              id="dm-first"
              placeholder="Ada"
              value={form.data.first_name}
              onChange={(e) => form.setData("first_name", e.target.value)}
            />
            {form.errors.first_name && <p className="text-xs text-destructive">{form.errors.first_name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dm-last">Last name</Label>
            <Input
              id="dm-last"
              placeholder="Lovelace"
              value={form.data.last_name}
              onChange={(e) => form.setData("last_name", e.target.value)}
            />
            {form.errors.last_name && <p className="text-xs text-destructive">{form.errors.last_name}</p>}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dm-su-email">Email</Label>
          <Input
            id="dm-su-email"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            value={form.data.email_address}
            onChange={(e) => form.setData("email_address", e.target.value)}
          />
          {form.errors.email_address && <p className="text-xs text-destructive">{form.errors.email_address}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dm-su-password">Password</Label>
          <Input
            id="dm-su-password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            value={form.data.password}
            onChange={(e) => form.setData("password", e.target.value)}
          />
          {form.errors.password && <p className="text-xs text-destructive">{form.errors.password}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dm-su-confirm">Confirm password</Label>
          <Input
            id="dm-su-confirm"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={form.data.password_confirmation}
            onChange={(e) => form.setData("password_confirmation", e.target.value)}
          />
          {form.errors.password_confirmation && <p className="text-xs text-destructive">{form.errors.password_confirmation}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={form.processing}>
          {form.processing ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </div>
  );
}

